'use client'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { colorType } from '@/lib/color/type'
import { colorSort } from '@/lib/color/util'
import { itemOptionType } from '@/lib/item/server'
import { sizeType } from '@/lib/size/type'
import { sizeSort } from '@/lib/size/util'
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react'
import { ItemOption } from '../../common/itemOption'

// ItemOptionTypeのcolor,sizeにorderを足した型
//
// #memo 不具合の対応が難しそうなためオミット
// 不具合はissueコメントを参照 https://github.com/flumo/ec-front/issues/161#issuecomment-2979006093
// type localColorType = colorWithOrderType
// type localSizeType = sizeWithOrderType
type localColorType = colorType
type localSizeType = sizeType
export type localItemOptionType = Omit<itemOptionType, 'color' | 'size'> & {
  color?: localColorType
  size?: localSizeType
}

type ItemOptionSelectProps = {
  itemOptions: localItemOptionType[]
  selectedOptions: localItemOptionType[]
  onChanged: (options: localItemOptionType[]) => void
  onSelecting: (selecting: boolean) => void
}

// selectedOptions: 選択済みオプションの配列、nullを含まないことを保証する
// onChanged: selectedOptions の変更を受け取るコールバック
// onSelecting: オプション選択中かどうかを表すフラグ。選択中は購入ボタンを無効化する（このコンポーネントの外で）
export const ItemOptionSelect: React.FC<ItemOptionSelectProps> = ({
  itemOptions,
  selectedOptions,
  onChanged,
  onSelecting,
}) => {
  // オプションのデータによってUIを分岐する
  const hasDefectiveData = useMemo(() => {
    const baseSizes: sizeType[] = []
    const baseColors: colorType[] = []
    const baseItemType: string[] = []
    itemOptions.forEach((option) => {
      if (
        option.size &&
        option.size.name !== '' &&
        !baseSizes.map((s) => s.size_id).includes(option.size.size_id)
      ) {
        baseSizes.push(option.size)
      }
      if (
        option.color &&
        option.color.name !== '' &&
        !baseColors.map((c) => c.color_id).includes(option.color.color_id)
      ) {
        baseColors.push(option.color)
      }
      if (option.name !== '' && !baseItemType.includes(option.name)) {
        baseItemType.push(option.name)
      }
    })

    return (
      (baseItemType.length > 0 && itemOptions.some((o) => o.name === '')) ||
      (baseSizes.length > 0 && itemOptions.some((o) => !o.size)) ||
      (baseColors.length > 0 && itemOptions.some((o) => !o.color))
    )
  }, [itemOptions])

  const optionsHaveDifferentPrice = itemOptions.some(
    (o) => o.price !== itemOptions[0].price,
  )

  if (hasDefectiveData) {
    return (
      <ItemOptionWithDefectiveData
        itemOptions={itemOptions}
        selectedOptions={selectedOptions}
        onChanged={onChanged}
        onSelecting={onSelecting}
      />
    )
  } else {
    return (
      <SelectItemOptionsIndividual
        itemOptions={itemOptions}
        optionsHaveDifferentPrice={optionsHaveDifferentPrice}
        selectedOptions={selectedOptions}
        onChanged={onChanged}
        onSelecting={onSelecting}
      />
    )
  }
}

const ItemOptionWithDefectiveData = ({
  itemOptions,
  selectedOptions,
  onChanged,
  onSelecting,
}: {
  itemOptions: localItemOptionType[]
  selectedOptions: localItemOptionType[]
  onChanged: (options: localItemOptionType[]) => void
  onSelecting: (selecting: boolean) => void
}) => {
  const selectedOption = selectedOptions[0]
  return (
    <>
      <div className="flex w-full">
        <Select
          defaultValue={selectedOption.custom_option_id.toString()}
          onValueChange={(v) => {
            onChanged([
              itemOptions.find((o) => o.custom_option_id === parseInt(v))!,
            ])
          }}
          onOpenChange={(v) => {
            setTimeout(() => {
              onSelecting(v)
            }, 500)
          }}
        >
          <SelectTrigger>
            <SelectValue>
              <ItemOption option={selectedOption} />
            </SelectValue>
          </SelectTrigger>
          <SelectContent className="w-full bg-white">
            {itemOptions.map((o) => (
              <SelectItem
                key={o.custom_option_id}
                value={o.custom_option_id.toString()}
              >
                <ItemOption option={o} />
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </>
  )
}

// オプション＋サイズ＋カラーで選択
// todo: 現状では不完全な実装になっている。selectedOptionsが変わっても、内部ステートに影響しないため。
const SelectItemOptionsIndividual = ({
  itemOptions,
  optionsHaveDifferentPrice,
  selectedOptions,
  onChanged,
  onSelecting,
}: {
  itemOptions: localItemOptionType[]
  optionsHaveDifferentPrice: boolean
  selectedOptions: localItemOptionType[]
  onChanged: (options: localItemOptionType[]) => void
  onSelecting: (selecting: boolean) => void
}) => {
  // オプション選択の中間データ。propsのselectedOptionsとの違いとして、nullableになっている
  const [selectedOptionsNullable, setSelectedOptionsNullable] = useState<
    (localItemOptionType | null)[]
  >([selectedOptions[0]])

  const [open, setOpen] = useState(false)

  useEffect(() => {
    const selecting =
      selectedOptionsNullable.length > 0 &&
      !selectedOptionsNullable.includes(null)
        ? open
        : true
    onSelecting(selecting)
    if (!selecting) {
      onChanged(
        selectedOptionsNullable.filter(
          (o) => o !== null,
        ) as localItemOptionType[],
      )
    }
  }, [selectedOptionsNullable, open])

  return (
    <>
      {selectedOptionsNullable.map((_, index) => (
        <IndividualOption
          key={index}
          itemOptions={itemOptions}
          index={index}
          selectedOptions={selectedOptionsNullable}
          setSelectedOptions={setSelectedOptionsNullable}
          optionsHaveDifferentPrice={optionsHaveDifferentPrice}
          setOpen={setOpen}
        />
      ))}
    </>
  )
}

const IndividualOption = ({
  itemOptions,
  index,
  selectedOptions,
  setSelectedOptions,
  setOpen,
}: {
  itemOptions: localItemOptionType[]
  index: number
  selectedOptions: (localItemOptionType | null)[]
  setSelectedOptions: Dispatch<SetStateAction<(localItemOptionType | null)[]>>
  optionsHaveDifferentPrice: boolean
  setOpen: Dispatch<SetStateAction<boolean>>
}) => {
  const baseSizes: localSizeType[] = []
  const baseColors: localColorType[] = []
  const baseItemType: string[] = []
  itemOptions.forEach((option) => {
    if (
      option.size &&
      option.size.name !== '' &&
      !baseSizes.map((s) => s.size_id).includes(option.size.size_id)
    ) {
      baseSizes.push(option.size)
    }
    if (
      option.color &&
      option.color.name !== '' &&
      !baseColors.map((c) => c.color_id).includes(option.color.color_id)
    ) {
      baseColors.push(option.color)
    }
    if (option.name !== '' && !baseItemType.includes(option.name)) {
      baseItemType.push(option.name)
    }
  })
  const [selectableSizes, setSelectableSizes] = useState<localSizeType[]>(
    getUniqueArray(itemOptions.map((o) => o.size!)),
  )
  const [selectableColors, setSelectableColors] = useState<localColorType[]>(
    getUniqueArray(itemOptions.map((o) => o.color!)),
  )
  const [selectableItemTypes, setSelectableItemTypes] = useState<string[]>(
    getUniqueArray(itemOptions.map((o) => o.name)),
  )
  const [selectedSize, setSelectedSize] = useState<localSizeType | null>(
    selectedOptions[index]?.size ?? null,
  )
  const [selectedColor, setSelectedColor] = useState<localColorType | null>(
    selectedOptions[index]?.color ?? null,
  )
  const [selectedItemType, setSelectedItemType] = useState<string | null>(
    selectedOptions[index]?.name ?? null,
  )

  return (
    <div className="w-full">
      <div className="my-2 w-full">
        <div className="flex w-full justify-start mt-2">
          {selectableItemTypes.length > 0 && baseItemType.length > 1 && (
            <Select
              defaultValue={selectableItemTypes[0]}
              onValueChange={(v) => {
                const selectableOption = itemOptions.filter((o) => o.name === v)
                setSelectedItemType(v)
                if (selectedColor && selectedSize) {
                  const selectableColors = selectableOption.map((o) => o.color!)
                  const selectableSizes = selectableOption.map((o) => o.size!)
                  setSelectableColors(getUniqueArray(selectableColors))
                  setSelectableSizes(getUniqueArray(selectableSizes))
                  setSelectedOptions(
                    selectedOptions.map((o, i) => {
                      const option = itemOptions.find(
                        (o) =>
                          o.color!.color_id === selectedColor.color_id &&
                          o.size!.size_id === selectedSize.size_id &&
                          o.name === v,
                      )
                      return i === index ? (option ? option : null) : o
                    }),
                  )
                } else if (selectedColor && !selectedSize) {
                  setSelectableColors(
                    getUniqueArray(selectableOption.map((o) => o.color!)),
                  )
                  setSelectedOptions(
                    selectedOptions.map((o, i) => {
                      const option = itemOptions.find(
                        (o) =>
                          o.color!.color_id === selectedColor.color_id &&
                          o.name === v,
                      )
                      return i === index ? (option ? option : null) : o
                    }),
                  )
                } else if (!selectedColor && selectedSize) {
                  setSelectableSizes(
                    getUniqueArray(selectableOption.map((o) => o.size!)),
                  )
                  setSelectedOptions(
                    selectedOptions.map((o, i) => {
                      const option = itemOptions.find(
                        (o) =>
                          o.size!.size_id === selectedSize.size_id &&
                          o.name === v,
                      )
                      return i === index ? (option ? option : null) : o
                    }),
                  )
                } else {
                  setSelectedOptions(
                    selectedOptions.map((o, i) => {
                      const option = itemOptions.find((o) => o.name === v)
                      return i === index ? (option ? option : null) : o
                    }),
                  )
                }
              }}
              onOpenChange={(v) => {
                setTimeout(() => {
                  setOpen(v)
                }, 500)
              }}
            >
              <SelectTrigger className="min-w-[140px] w-[60%]">
                <SelectValue placeholder={'カテゴリ'} />
              </SelectTrigger>
              <SelectContent style={{ background: '#fff', zIndex: '100' }}>
                {/*重複を削除したあとの配列を使う*/}
                {selectableItemTypes.map((type) => (
                  <SelectItem key={type} value={type}>
                    {type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
        <div className="flex justify-start w-full mt-2 mb-2">
          {selectableSizes.length > 0 && baseSizes.length > 0 && (
            <div className="flex justify-center w-[33%] items-center mr-2">
              <Select
                defaultValue={selectableSizes[0].size_id.toString()}
                onValueChange={(v) => {
                  const selectableOption = itemOptions.filter(
                    (o) => o.size!.size_id === parseInt(v),
                  )
                  setSelectedSize(
                    selectableSizes.find((s) => s.size_id === parseInt(v)) ||
                      null,
                  )
                  if (selectedColor && selectedItemType) {
                    setSelectableColors(
                      getUniqueArray(selectableOption.map((o) => o.color!)),
                    )
                    setSelectableItemTypes(
                      getUniqueArray(selectableOption.map((o) => o.name)),
                    )
                    setSelectedOptions(
                      selectedOptions.map((o, i) => {
                        const option = itemOptions.find(
                          (o) =>
                            o.color!.color_id === selectedColor.color_id &&
                            o.size!.size_id === parseInt(v) &&
                            o.name === selectedItemType,
                        )
                        return i === index ? (option ? option : null) : o
                      }),
                    )
                  } else if (selectedColor && !selectedItemType) {
                    setSelectableColors(
                      getUniqueArray(selectableOption.map((o) => o.color!)),
                    )
                    setSelectedOptions(
                      selectedOptions.map((o, i) => {
                        const option = itemOptions.find(
                          (o) =>
                            o.color!.color_id === selectedColor.color_id &&
                            o.size!.size_id === parseInt(v),
                        )
                        return i === index ? (option ? option : null) : o
                      }),
                    )
                  } else if (!selectedColor && selectedItemType) {
                    setSelectableItemTypes(
                      getUniqueArray(selectableOption.map((o) => o.name)),
                    )
                    setSelectedOptions(
                      selectedOptions.map((o, i) => {
                        const option = itemOptions.find(
                          (o) =>
                            o.size!.size_id === parseInt(v) &&
                            o.name === selectedItemType,
                        )
                        return i === index ? (option ? option : null) : o
                      }),
                    )
                  } else {
                    setSelectedOptions(
                      selectedOptions.map((o, i) => {
                        const option = itemOptions.find(
                          (o) => o.size!.size_id === parseInt(v),
                        )
                        return i === index ? (option ? option : null) : o
                      }),
                    )
                  }
                }}
                onOpenChange={(v) => {
                  setTimeout(() => {
                    setOpen(v)
                  }, 500)
                }}
              >
                <SelectTrigger className="w-[160px] h-[32px]">
                  <SelectValue placeholder={'サイズ'} />
                </SelectTrigger>
                <SelectContent style={{ background: '#fff', zIndex: '100' }}>
                  {sizeSort(selectableSizes).map((size) => (
                    <SelectItem
                      key={size.size_id}
                      value={size.size_id.toString()}
                    >
                      {size.name} {size.sex}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {selectableColors.length > 0 && baseColors.length > 0 && (
            <div className="flex justify-center w-[33%] items-center mr-2">
              <Select
                defaultValue={selectableColors[0].color_id.toString()}
                onValueChange={(v) => {
                  const selectableOption = itemOptions.filter(
                    (o) => o.color!.color_id === parseInt(v),
                  )
                  setSelectedColor(
                    selectableColors.find((c) => c.color_id === parseInt(v)) ||
                      null,
                  )
                  if (selectedSize && selectedItemType) {
                    setSelectableSizes(
                      getUniqueArray(selectableOption.map((o) => o.size!)),
                    )
                    setSelectableItemTypes(
                      getUniqueArray(selectableOption.map((o) => o.name)),
                    )
                    setSelectedOptions(
                      selectedOptions.map((o, i) => {
                        const option = itemOptions.find(
                          (o) =>
                            o.size!.size_id === selectedSize.size_id &&
                            o.color!.color_id === parseInt(v) &&
                            o.name === selectedItemType,
                        )
                        return i === index ? (option ? option : null) : o
                      }),
                    )
                  } else if (selectedSize && !selectedItemType) {
                    setSelectableSizes(
                      getUniqueArray(selectableOption.map((o) => o.size!)),
                    )
                    setSelectedOptions(
                      selectedOptions.map((o, i) => {
                        const option = itemOptions.find(
                          (o) =>
                            o.size!.size_id === selectedSize.size_id &&
                            o.color!.color_id === parseInt(v),
                        )
                        return i === index ? (option ? option : null) : o
                      }),
                    )
                  } else if (!selectedSize && selectedItemType) {
                    setSelectableItemTypes(
                      getUniqueArray(selectableOption.map((o) => o.name)),
                    )
                    setSelectedOptions(
                      selectedOptions.map((o, i) => {
                        const option = itemOptions.find(
                          (o) =>
                            o.color!.color_id === parseInt(v) &&
                            o.name === selectedItemType,
                        )
                        return i === index ? (option ? option : null) : o
                      }),
                    )
                  } else {
                    setSelectedOptions(
                      selectedOptions.map((o, i) => {
                        const option = itemOptions.find(
                          (o) => o.color!.color_id === parseInt(v),
                        )
                        return i === index ? (option ? option : null) : o
                      }),
                    )
                  }
                }}
                onOpenChange={(v) => {
                  setTimeout(() => {
                    setOpen(v)
                  }, 500)
                }}
              >
                <SelectTrigger className="w-[160px] h-[32px]">
                  <SelectValue placeholder={'カラー'} />
                </SelectTrigger>
                <SelectContent style={{ background: '#fff', zIndex: '100' }}>
                  {colorSort(selectableColors).map((color) => (
                    <SelectItem
                      key={color.color_id}
                      value={color.color_id.toString()}
                      className="z-60"
                      tabIndex={100}
                    >
                      {color.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <div className="flex justify-end w-full">
          {selectedOptions[index] &&
          selectedOptions[index]!.discounted_price !== undefined &&
          selectedOptions[index]!.discounted_price > 0 ? (
            <div
              className="text-sm font-semibold text-center"
              style={{ color: '#424242' }}
            >
              {selectedOptions[index]?.discounted_price}{' '}
              <span className="text-xs font-light"> 円 (税込)</span>
              <div className="line-through text-xs text-gray-500">
                <span className="original-price-text">
                  {selectedOptions[index]!.price.toLocaleString()}
                </span>
                <span className="price-yen font-light"> 円 (割引前)</span>
              </div>
            </div>
          ) : (
            <span className="text-sm font-semibold text-gray-900">
              {selectedOptions[index]!.price.toLocaleString()}{' '}
              <span className="text-sm text-gray-900 ml-4">円</span>
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

const getUniqueArray = (options: any[]) => {
  return Array.from(
    new Set(
      options
        .filter((item) => item !== undefined) // undefined を除外
        .map((item) => {
          try {
            return JSON.stringify(item)
          } catch (e) {
            // JSON.stringify できない値の場合はスキップ
            return ''
          }
        })
        .filter((item) => item !== null), // null を除外
    ),
  ).map((item) => JSON.parse(item))
}
